// Marketing pricing page (reachable from Home). Layout follows the Goodnotes pricing feel:
// headline → monthly/annual toggle → plan cards (Pro highlighted) → comparison table → final CTA,
// with generous whitespace. Web is free for now; the subscriptions are introduced here.
import { useState } from "react";
import { useApp } from "../store/session";
import { useAuth } from "../auth/useAuth";
import { startCheckout, type PlanKey } from "../sync/api";

const PLANS = [
  {
    key: "free",
    name: "Free",
    monthly: 0,
    yearly: 0,
    tagline: "まずは無料で。1冊でじっくりお試し。",
    features: [
      "本を1冊まで取り込み",
      "答えの自動検出・赤マスク／赤シート",
      "目次・縦読み／横読み・倍率調整",
      "AI問題（○×・4択）の自動生成：月1回",
    ],
    highlight: false,
    coming: false,
  },
  {
    key: "standard",
    name: "Standard",
    monthly: 300,
    yearly: 3000,
    tagline: "基本の暗記機能をすべて。たっぷり10冊。",
    features: [
      "本を10冊まで取り込み",
      "答えの自動検出・赤マスク／赤シート",
      "目次・縦読み／横読み・倍率調整",
      "AI問題（○×・4択）の自動生成：月10回",
    ],
    highlight: false,
    coming: false,
  },
  {
    key: "pro",
    name: "Pro",
    monthly: 600,
    yearly: 6000,
    tagline: "端末をまたいでどこでも。",
    features: [
      "本を無制限に取り込み",
      "クラウド保存・全端末／全OSで進捗同期",
      "AI問題（○×・4択）の自動生成：月30回",
    ],
    highlight: false,
    coming: false,
  },
  {
    key: "premium",
    name: "Premium",
    monthly: 980,
    yearly: 9800,
    tagline: "Pro のすべて＋AIが復習を最適化。",
    features: [
      "Pro のすべて",
      "「今日の復習」— 間違えやすい問題を最適なタイミングに再出題",
      "AI問題（○×・4択）の自動生成：月100回",
      "7日間の無料トライアル",
    ],
    highlight: true,
    coming: false,
  },
];

const COMPARE = [
  { label: "本の取り込み", free: "1冊", standard: "10冊", pro: "無制限", premium: "無制限" },
  { label: "答えの自動検出・赤マスク／赤シート", free: "○", standard: "○", pro: "○", premium: "○" },
  { label: "目次・縦横読み・倍率調整", free: "○", standard: "○", pro: "○", premium: "○" },
  {
    label: "AI問題の生成（○×・4択／月あたり）",
    free: "1回",
    standard: "10回",
    pro: "30回",
    premium: "100回",
  },
  { label: "間違えた問題だけ復習", free: "○", standard: "○", pro: "○", premium: "○" },
  { label: "クラウド保存・全端末で同期", free: "—", standard: "—", pro: "○", premium: "○" },
  { label: "「今日の復習」（SRS）", free: "—", standard: "—", pro: "—", premium: "○" },
  { label: "無料トライアル", free: "—", standard: "—", pro: "—", premium: "7日間" },
];

const yen = (n: number) => `¥${n.toLocaleString("ja-JP")}`;

export function Pricing() {
  const setView = useApp((s) => s.setView);
  const user = useAuth((s) => s.user);
  const [annual, setAnnual] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const start = () => setView({ name: "decks" });

  // Paid plans → hosted Stripe Checkout (sign-in required first). Free → straight into the app.
  const choose = async (planKey: string) => {
    setErr("");
    if (planKey === "free") return start();
    if (!user) return setView({ name: "login" });
    setBusy(planKey);
    try {
      await startCheckout(planKey as PlanKey, annual ? "yearly" : "monthly");
      // success → the browser redirects to Stripe; nothing else to do here.
    } catch (e) {
      const m = e instanceof Error ? e.message : "";
      setErr(
        m === "billing_unavailable"
          ? "現在お申し込みを受け付けていません。時間をおいて再度お試しください。"
          : "お申し込みの開始に失敗しました。時間をおいて再度お試しください。",
      );
      setBusy(null);
    }
  };

  const ctaLabel = (planKey: string): string => {
    if (busy === planKey) return "処理中…";
    if (planKey === "free") return "無料で始める";
    return user ? "このプランにする" : "ログインして始める";
  };

  return (
    <div className="pricing">
      <section className="pricing-hero">
        <h1 className="pricing-title">学習に合わせて選べる料金プラン</h1>
        <p className="pricing-lead">無料で1冊から。Premiumは初回7日間無料・いつでも解約できます。</p>
        <div className="pricing-toggle" role="group" aria-label="請求サイクル">
          <button className={annual ? "" : "on"} onClick={() => setAnnual(false)}>
            月額
          </button>
          <button className={annual ? "on" : ""} onClick={() => setAnnual(true)}>
            年額<span className="save">2ヶ月分お得</span>
          </button>
        </div>
      </section>

      <section className="plan-cards">
        {PLANS.map((p) => (
          <div className={`plan-card ${p.highlight ? "highlight" : ""}`} key={p.key}>
            {p.coming ? (
              <span className="plan-badge">近日</span>
            ) : (
              p.highlight && <span className="plan-badge">おすすめ</span>
            )}
            <h2 className="plan-card-name">{p.name}</h2>
            <p className="plan-card-tagline">{p.tagline}</p>
            <p className="plan-card-price">
              {p.monthly === 0 ? (
                <span className="amount">¥0</span>
              ) : (
                <>
                  <span className="amount">{yen(annual ? p.yearly : p.monthly)}</span>
                  <span className="period"> / {annual ? "年" : "月"}</span>
                </>
              )}
            </p>
            <p className="plan-card-sub muted small">
              {p.monthly === 0
                ? "ずっと無料"
                : annual
                  ? `月あたり約 ${yen(Math.round(p.yearly / 12))}`
                  : `年額 ${yen(p.yearly)}`}
            </p>
            <ul className="plan-card-features">
              {p.features.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <button
              className={`btn ${p.highlight ? "primary" : "ghost"} big-cta`}
              onClick={() => void choose(p.key)}
              disabled={busy !== null}
            >
              {ctaLabel(p.key)}
            </button>
          </div>
        ))}
      </section>

      {err && <p className="pricing-note center auth-msg">{err}</p>}
      <p className="pricing-note center muted small">
        お支払いは Stripe の安全な決済ページで行います（カード情報は当社サーバーを経由しません）。
        ご購入・解約には無料アカウントへのログインが必要です。
      </p>

      <section className="compare-section">
        <h2 className="section-title">プランの比較</h2>
        <div className="compare-wrap">
          <table className="compare-table">
            <thead>
              <tr>
                <th></th>
                <th>Free</th>
                <th>Standard</th>
                <th className="pro-col">Pro</th>
                <th>Premium</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((r) => (
                <tr key={r.label}>
                  <td className="compare-feature">{r.label}</td>
                  <td>{r.free}</td>
                  <td>{r.standard}</td>
                  <td className="pro-col">{r.pro}</td>
                  <td>{r.premium}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="pricing-final">
        <h2>まずは無料で試してみましょう</h2>
        <button className="btn primary big-cta" onClick={start}>
          無料で始める
        </button>
        <p className="muted small center">
          Premiumには初回のみ7日間の無料トライアルが付きます。トライアル終了時に解約しない限り、料金が
          自動で請求されます。解約はいつでも可能です。
        </p>
      </section>
    </div>
  );
}
